import axios from 'axios';
import jwt_decode from 'jwt-decode';
import { toFormData } from './utils';
import { generatePath } from 'react-router-dom';
import { getLanguage, translate } from '../i18n';

const baseUrl =
  process.env.NODE_ENV === 'development'
    ? 'https://demo-app.o2reco.ir/api/v1/'
    : '/api/v1/';

const api = axios.create({
  baseURL: baseUrl
});

// backend answers bilingually only to requests that identify as the app
api.defaults.headers.common['X-Client'] = 'app';

// used by the backend before login (and for 401s); ignored once the user is known
api.interceptors.request.use((request) => {
  request.headers['Accept-Language'] = getLanguage();
  return request;
});

// pages toast `detail` as is; translated server messages are strings, while
// validation errors (422 arrays) and bare 5xx pages carry nothing showable
api.interceptors.response.use(undefined, (error) => {
  const { response } = error;
  if (response && typeof response.data?.detail !== 'string') {
    response.data = {
      ...(response.data && typeof response.data === 'object'
        ? response.data
        : {}),
      detail: translate('common.error')
    };
  }
  return Promise.reject(error);
});

const rest = {
  LOGIN: '/user/login',
  PROFILE: '/user/:id',
  USER_PROFILE: '/user/u/',
  EDIT_PROFILE: '/user/me',
  MY_PROFILE: '/user/me',
  CHANGE_PASS: '/user/change_password',
  ANNOUNCEMENTS: '/announcement/',
  ANNOUNCEMENT: '/announcement/:id',
  KUDOS: '/kudos/',
  KUDOS_BY_ID: '/kudos/:id',
  SEARCH: '/user/',
  ADD_AVATAR: '/user/avatar',
  DEL_AVATAR: '/user/avatar/:id',
  KUDOS_CARDS: '/kudos_card/',
  REWARDS: '/reward/',
  REWARD_CATEGORIES: '/reward_category/',
  MY_REWARDS: '/reward_order/',
  LEADERBOARD_RECEIVER_COUNT: '/leaderboard/receiver_by_count',
  LEADERBOARD_RECEIVER_SCORE: '/leaderboard/receiver_by_score',
  LEADERBOARD_SENDER_COUNT: '/leaderboard/sender_by_count',
  LEADERBOARD_SENDER_SCORE: '/leaderboard/sender_by_score',
  INVOLVED_USERS: '/involved-users',
  FEEDBACK_REQUEST: '/feedback_request/',
  FEEDBACK_REQUEST_BYID: '/feedback_request/:id',
  FEEDBACK_COMMENTS: '/feedback_comments/',
  NOTIFICATION_COUNT: '/notification/unseen_count/',
  NOTIFICATION: '/notification/',
  TODO: '/todo/',
  TODOBYID: '/todo/:id',
  KUDOS_LIKE: '/kudos_like/:id',
  MOOD_TRACKER: '/mood_tracker/',
  MOOD_COMMENT: '/mood_comment/',
  MOOD_TRACKER_AVG: '/mood_tracker/avg/',
  MOOD_TRACKER_REPORT: '/mood_tracker/report/',
  USER_SETTING: '/user_setting/',
  DEVICE: '/device/',
  SURVEY: '/survey/',
  SURVEY_BYID: '/survey/:id',
  SURVEY_QUESTION: '/survey_question/',
  SURVEY_ANSWER: '/survey_answer/',
  SURVEY_RESPONSE: '/survey_response/',
  SURVEY_RESPONSE_SUBMIT: '/survey_response/:id',

  MOOD_CHAT: '/mood_chat/:mood_id/',
  POST_MOOD_CHAT: '/mood_chat/'
};

/***
 * @param {'POST' | 'PATCH' | 'GET' | 'DELETE'} type
 * @param {String} route
 * @param {Object} [body]
 * @param {Object} [params]
 * @param {'json' | 'formData' | 'text'} [headerType]
 * @returns {*}
 */
const apiCall = (type, route, body, params, headerType) => {
  let req;
  let postData;
  let deleteConfig;

  switch (type) {
    case 'POST':
    case 'PATCH':
      if (headerType === 'formData') {
        postData = toFormData(body);
      } else {
        postData = body;
      }
      req =
        type === 'POST'
          ? api.post(route, postData, config(headerType ? headerType : 'json'))
          : api.patch(
              route,
              postData,
              config(headerType ? headerType : 'json')
            );
      break;

    case 'GET':
      req = api.get(route, config('json', params)); // data as params
      break;

    case 'DELETE':
      deleteConfig = {
        data: body,
        ...config('json', params)
      };
      req = api.delete(route, deleteConfig);
      break;

    default:
      throw new Error('wrong api method');
  }

  return req
    .then((response) => response?.data)
    .catch((err) => {
      throw err;
    });
};

const set_token = (token) => {
  return localStorage.setItem('token', token);
};

const get_token = () => {
  return localStorage.getItem('token');
};

const remove_token = () => {
  return localStorage.removeItem('token');
};

const get_user_info = () => {
  const token = get_token();
  if (token) {
    const { user_id, exp } = jwt_decode(token);
    return {
      id: user_id,
      exp
    };
  } else {
    return null;
  }
};

const get_now = () => {
  const date = new Date().getTime();
  return (date - (date % 1000)) / 1000;
};

/***
 * @param {'formData' | 'json' | 'text'} type
 * @param {Object }params
 * @returns {{headers: {Authorization: string, Accept: string, 'Content-Type'}, params: {}, timeout: number}}
 */
const config = (type, params = {}) => {
  let contentType;
  switch (type) {
    case 'formData':
      contentType = 'multipart/form-data';
      break;

    default:
    case 'json':
      contentType = 'application/json';
      break;

    case 'text':
      contentType = 'text/plain';
      break;
  }

  return {
    timeout: 20000,
    params: params,
    headers: {
      Accept: '*/*',
      'Content-Type': contentType,
      Authorization: 'bearer ' + get_token()
    }
  };
};

//uses in AnnouncementList page and Home page
export async function announcementsListRequest(
  { pageParam, myDepartment },
  limit = 5
) {
  return await api.get(
    '/announcement/',
    config('json', {
      page: pageParam,
      limit: limit,
      my_department: myDepartment
    })
  );
}

//use in Search page
export async function searchUsersRequest(page, q, departmentId) {
  const query = {
    page,
    q
  };

  if (departmentId) {
    query.department_id = departmentId;
  }
  return await api.get('/user/', config('json', query));
}

export async function involvedUsersRequest() {
  return await api.get('/involved-users', config('json'));
}

//uses in Profile page and Home page
export async function kudosListRequest({
  last_id,
  received_kudos,
  given_kudos,
  user_id,
  card_id = null,
  my_department,
  sum_of_values
}) {
  return await api.get(
    '/kudos/',
    config('json', {
      last_id,
      received_kudos,
      given_kudos,
      user_id,
      card_id,
      my_department,
      sum_of_values
    })
  );
}

//uses in Profile page
//uses in Header and SideBar
export async function getMyProfileRequest() {
  const response = await api.get('/user/me', config('json'));
  return response.data;
}

export async function deleteKudosById({ id }) {
  const response = await api.delete(`/kudos/${id}`, config('json'));
  return response.data;
}

//uses in Header and SideBar
export async function getNotificationsCountRequest() {
  return await api.get('/notification/unseen_count/', config('json'));
}

export async function sendMoodRequest({
  mood,
  comment = null,
  reason = null,
  anonymous
}) {
  return await api.post(
    '/mood_tracker/',
    { mood, comment, reason, anonymous },
    config('json')
  );
}
export async function getMoodReasons() {
  const response = await api.get(`/mood_reasons/`, config('json'));
  return response.data;
}

//uses in editProfile and welcome page
export async function addAvatarRequest({ file }) {
  return await api.post(
    '/user/avatar',
    {
      file
    },
    config('formData')
  );
}

export async function deleteAvatarRequest({ id }) {
  return await api.delete(
    generatePath('/user/avatar/:id', { id }),
    config('json')
  );
}

//uses in GiveKudos page
export function kudosCardsRequest(isValue = null) {
  return api.get(rest.KUDOS_CARDS, config('json', { is_value: isValue }));
}

export async function getOrganizationFeaturesRequest() {
  const response = api.get('/organization_features/', config('json'));

  return response;
}

export async function getUserSettingRequest() {
  const response = await api.get('/user_setting/', config('json'));
  return response.data;
}

export async function getFeatureListRequest() {
  return await api.get('/organization_features/', config('json'));
}

export {
  api,
  rest,
  baseUrl,
  config,
  set_token,
  get_token,
  remove_token,
  get_user_info,
  get_now,
  apiCall
};
